import React from 'react';
import shippingOptions from '../../data/shippingOptions.json';
import { shippingCarriers } from '../../data/shippingCarriers';
import { US_STATES } from '../../data/usStates';
import { formatDeliveryDate } from '../../utils/orderDates';

export default function OrderDeliveryFields({
  t,
  locale,
  inputClass,
  selectClass,
  address,
  setAddress,
  addressCity,
  setAddressCity,
  addressState,
  setAddressState,
  addressZipCode,
  setAddressZipCode,
  addressEdited,
  setAddressEdited,
  savedAddress,
  hasDeliveryAddress,
  useDefaultAddress,
  preferredCarrier,
  setPreferredCarrier,
  submitting,
  shippingHelpOpen,
  setShippingHelpOpen,
  shippingSpeed,
  setShippingSpeed,
  updateShipping,
  deliveryDate,
  shippingState,
  shippingNotice,
  onRetryShipping,
}) {
  return (
    <>
      <div className="mb-4">
        <label
          htmlFor="new-order-delivery-address"
          className="block text-[12px] font-medium text-[#0f172a] mb-1"
        >
          {t('Delivery Address')}
        </label>
        <input
          id="new-order-delivery-address"
          value={address}
          onChange={(e) => {
            setAddressEdited(true);
            setAddress(e.target.value);
            if (!e.target.value.trim()) {
              setAddressCity('');
              setAddressState('');
              setAddressZipCode('');
            }
          }}
          placeholder={t('Street address')}
          maxLength={120}
          className={inputClass}
        />
        {savedAddress && (
          <p className="text-[11px] text-[#64748b] mt-1">
            {t(
              addressEdited
                ? 'Address changes apply to this order only.'
                : 'Your saved delivery address. You can edit it for this order.'
            )}
          </p>
        )}
        {hasDeliveryAddress && (
          <div className="mt-3">
            <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
              <div>
                <label
                  htmlFor="new-order-address-city"
                  className="block text-[12px] font-medium text-[#0f172a] mb-1"
                >
                  {t('City')} *
                </label>
                <input
                  id="new-order-address-city"
                  aria-label={t('Delivery city')}
                  value={addressCity}
                  maxLength={80}
                  onChange={(e) => {
                    setAddressEdited(true);
                    setAddressCity(e.target.value);
                  }}
                  className={inputClass + ' min-w-0'}
                />
              </div>
              <div>
                <label
                  htmlFor="new-order-address-state"
                  className="block text-[12px] font-medium text-[#0f172a] mb-1"
                >
                  {t('State')} *
                </label>
                <select
                  id="new-order-address-state"
                  aria-label={t('Delivery state')}
                  value={addressState}
                  onChange={(e) => {
                    setAddressEdited(true);
                    setAddressState(e.target.value);
                  }}
                  className={selectClass + ' min-w-0'}
                >
                  <option value="">{t('Select state')}</option>
                  {US_STATES.map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="new-order-address-zip"
                  className="block text-[12px] font-medium text-[#0f172a] mb-1"
                >
                  {t('ZIP code')} *
                </label>
                <input
                  id="new-order-address-zip"
                  aria-label={t('Delivery ZIP code')}
                  value={addressZipCode}
                  maxLength={20}
                  onChange={(e) => {
                    setAddressEdited(true);
                    setAddressZipCode(e.target.value);
                  }}
                  className={inputClass + ' min-w-0'}
                />
              </div>
            </div>
            <button
              type="button"
              className="text-[11px] text-[#0089ff] underline mt-2"
              onClick={useDefaultAddress}
            >
              {t('Use default address')}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_1.25fr] gap-3 mb-4">
        <div className="min-w-0">
          <label
            htmlFor="new-order-preferred-carrier"
            className="block text-[12px] font-medium text-[#0f172a] mb-1"
          >
            {t('Preferred shipper')}
          </label>
          <select
            id="new-order-preferred-carrier"
            value={preferredCarrier}
            onChange={(e) => setPreferredCarrier(e.target.value)}
            disabled={submitting}
            className={selectClass}
          >
            {shippingCarriers.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-0">
          <div className="relative flex items-center gap-1 mb-1">
            <label
              htmlFor="new-order-shipping-option"
              className="text-[12px] font-medium text-[#0f172a]"
            >
              {t('Shipping option')}
            </label>
            <button
              type="button"
              aria-label={t('Shipping information')}
              aria-expanded={shippingHelpOpen}
              aria-controls="new-order-shipping-help"
              aria-describedby={shippingHelpOpen ? 'new-order-shipping-help' : undefined}
              onClick={() => setShippingHelpOpen((value) => !value)}
              onBlur={() => setShippingHelpOpen(false)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && shippingHelpOpen) {
                  event.stopPropagation();
                  setShippingHelpOpen(false);
                }
              }}
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[#64748b] hover:text-[#0089ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0089ff]"
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[16px]">
                info
              </span>
            </button>
            {shippingHelpOpen && (
              <div
                id="new-order-shipping-help"
                role="tooltip"
                className="absolute left-0 top-full mt-2 z-20 w-[320px] max-w-[calc(100vw-4rem)] rounded-[8px] border border-[#d6dce8] bg-white p-3 text-[12px] leading-relaxed text-[#475569] shadow-lg"
              >
                {t(
                  'Choose a shipping service to calculate the estimated delivery date automatically. Orders at or after 4 PM Mountain Time move to the next shipping day. Shipping and delivery are Monday through Friday. Delivery times are not guaranteed.'
                )}
              </div>
            )}
          </div>
          <select
            id="new-order-shipping-option"
            value={shippingSpeed}
            onChange={(e) => {
              setShippingSpeed(e.target.value);
              updateShipping({ shippingSpeed: e.target.value });
            }}
            disabled={submitting}
            className={selectClass}
          >
            <option value="">{t('Choose a shipping service')}</option>
            {shippingOptions.map((option) => (
              <option key={option} value={option}>
                {t(option)}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-0">
          <label
            htmlFor="new-order-estimated-delivery-date"
            className="block text-[12px] font-medium text-[#0f172a] mb-1"
          >
            {t('Estimated Delivery Date')}
          </label>
          <input
            id="new-order-estimated-delivery-date"
            type="text"
            readOnly
            value={deliveryDate ? formatDeliveryDate(deliveryDate, locale) : ''}
            placeholder={t('Calculated automatically')}
            disabled={submitting}
            className={inputClass + ' bg-[#f7f8fb]'}
          />
        </div>
      </div>

      <div aria-live="polite" className="mb-4 text-[12px] text-[#475569]">
        {shippingState === 'loading' && <p>{t('Checking delivery availability...')}</p>}
        {shippingNotice && <p>{t(shippingNotice)}</p>}
        {shippingState === 'error' && (
          <div role="alert">
            <p>
              {t(
                'Could not confirm shipping availability. Please try again before placing the order.'
              )}
            </p>
            <button
              type="button"
              className="text-[#0089ff] underline"
              disabled={submitting}
              onClick={onRetryShipping}
            >
              {t('Check delivery again')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
